// App Store
import { app } from './utils/store'

// User Detect
import { DetectionManager } from './utils/detect'

import { Navigation, Footer } from './modules/Navigation'
import AsyncLoad from './classes/AsyncLoad'

class App {
    constructor() {
        app.init = true
        app.bundle = false
        app.root = document.body
        app.viewport = document.querySelector('[data-app-viewport]')
        app.content = document.getElementById("app-content")
        app.images = document.querySelectorAll('[data-src][data-webp]')
        app.images = app.images.forEach((image) => {
            return new AsyncLoad({ element: image })
        })

        this.modules()
    }

    modules = () => {
        const critical = [
            {
                name: 'navigation',
                selector: '[data-navigation]',
                check: (element) => (element.querySelector('#menu-toggler') && element.querySelector('[data-navigation-menu]')),
                load: (element) => {
                    new Navigation()
                }
            },
            {
                name: 'footer',
                selector: 'footer',
                check: (element) => (element.querySelector('#select-language-footer') || element.querySelector('#newsletter')),
                load: (element) => {
                    new Footer()
                }
            },
            {
                name: 'sidebar',
                selector: 'aside.account-sidebar',
                check: (element) => element.querySelector('.user'),
                load: async (element) => { 
                    await import(
                        /* webpackChunkName: "sidebar" */
                        /* webpackPrefetch: true */
                        './modules/Sidebar'
                    ).then((module) => {
                        new module.Sidebar()
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
        ]

        critical.forEach((module, index) => {
            document.querySelectorAll(module.selector).forEach((element) => {
                if (module.check(element)) {
                    module.load(element)
                }
            })
        })

        const modules = [
            {
                name: 'buttons',
                selector: '[class^="btn-"]',
                check: (element) => element.classList.contains('btn-hexagon'),
                load: async (element) => { 
                    await import(
                        /* webpackChunkName: "buttons" */
                        /* webpackPrefetch: true */
                        './modules/Buttons'
                    ).then((module) => {
                        new module.HexagonButtonManager({ element: element })
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'accordions',
                selector: '.accordion-button',
                check: (element) => element.classList.contains('accordion-button'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "accordions" */
                        /* webpackPrefetch: true */
                        './classes/Accordion'
                    ).then((module) => {
                        new module.AccordionManager.create(element)
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'dropdowns',
                selector: '[data-bs-toggle="dropdown"]',
                check: (element) => element.getAttribute('data-bs-toggle') === 'dropdown',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "dropdowns" */
                        /* webpackPrefetch: true */
                        './classes/Dropdown'
                    ).then((module) => {
                        module.DropdownManager.create(element)
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'marquee',
                selector: '[data-slider="marquee"]',
                check: (element) => element.getAttribute('data-slider') === 'marquee',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "marquee" */
                        /* webpackPrefetch: true */
                        './modules/Marquee'
                    ).then((module) => {
                        new module.MarqueeManager({ element: element })
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'modals',
                selector: '.modal',
                check: (element) => element.hasAttribute('data-modal'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "modals" */
                        /* webpackPrefetch: true */
                        './classes/Modal'
                    ).then((module) => {
                        // Funzione per inizializzare i pulsanti modal
                        const initModals = () => {
                            document.querySelectorAll('[data-modal-target]').forEach((entry) => { 
                                // Evita di reinizializzare pulsanti già inizializzati
                                if (!entry.hasAttribute('data-modal-initialized')) {
                                    new module.ModalManager.toggle(entry);
                                    entry.setAttribute('data-modal-initialized', 'true');
                                }
                            });
                            document.querySelectorAll('[data-modal-close]').forEach((entry) => { 
                                if (!entry.hasAttribute('data-modal-close-initialized')) {
                                    new module.ModalManager.close(entry);
                                    entry.setAttribute('data-modal-close-initialized', 'true');
                                }
                            });
                        };
                        
                        // Inizializza i modali al caricamento
                        initModals();
                        
                        // Esponi funzione globale per reinizializzare dopo AJAX
                        window.reinitModals = () => {
                            initModals();
                        };
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'sliders',
                selector: '[data-slider]',
                check: (element) => element.getAttribute('data-slider') === 'milestones' || element.getAttribute('data-slider') === 'phases',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "sliders" */
                        /* webpackPrefetch: true */
                        './modules/Slider'
                    ).then((module) => {
                        new module.SliderManager({ element: element, options: element.hasAttribute('data-options') ? JSON.parse(element.getAttribute('data-options')) : null })
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'carousels',
                selector: '[data-carousel="overview"]',
                check: (element) => element.getAttribute('data-carousel') === 'overview',
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "carousels" */
                        /* webpackPrefetch: true */
                        './modules/Carousel'
                    ).then((module) => {
                        new module.CarouselManager({ element: element, options: element.hasAttribute('data-options') ? JSON.parse(element.getAttribute('data-options')) : null })
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'storelocator',
                selector: '#storelocator-search',
                check: (element) => !!element,
                load: async () => {
                    await import(
                        /* webpackChunkName: "storelocator" */
                        /* webpackPrefetch: true */
                        './modules/StoreLocator'
                    ).then((module) => {
                        new module.StoreLocator()
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            },
            {
                name: 'maps',
                selector: '[data-map]',
                check: (element) => element.getAttribute('data-map'),
                load: async (element) => {
                    await import(
                        /* webpackChunkName: "maps" */
                        /* webpackPrefetch: true */
                        './modules/Maps'
                    ).then((module) => {
                        /* Map with single marker */
                        if(element.hasAttribute('data-map') && element.getAttribute('data-map') === 'single-marker') {
                            if (element.dataset.location) {
                                let location = JSON.parse(element.dataset.location)
                    
                                if (location.lat && location.lng) {
                                    let store = {
                                        id: element.dataset.id,
                                        title: element.dataset.title,
                                        city: location.city,
                                        address: location.address,
                                        coordinates: {
                                            lat: parseFloat(location.lat),
                                            lng: parseFloat(location.lng),
                                        },
                                        sheet: location.sheet,
                                    }
                                    
                                    let map = new module.Maps({
                                        element: element,
                                        zoom: {
                                            value: 12,
                                            controls: {
                                                enable: false,
                                            }
                                        },
                                        coordinates: {
                                            lat: store.coordinates.lat,
                                            lng: store.coordinates.lng
                                        },
                                        ui: false,
                                        wheel: false,
                                        fullscreen: false,
                                        streetView: false,
                                        mapTypeControl: false,
                                        draggable: false,
                                        popup: true,
                                        stores: [store],
                                    })
                    
                                    map.create({
                                        search: null,
                                        markers: true,
                                    }, true)
                                }
                            }
                        }

                        /* Store locator map (overlay on SVG fallback) */
                        if (element.hasAttribute('data-map') && element.getAttribute('data-map') === 'storelocator') {
                            let storesData = []
                            const dataNode = document.getElementById('storelocator-data')
                            try {
                                storesData = dataNode ? JSON.parse(dataNode.textContent) : []
                            } catch (error) {
                                storesData = []
                            }

                            const stores = (Array.isArray(storesData) ? storesData : []).map((entry) => {
                                const lat = parseFloat(entry.lat != null ? entry.lat : entry.latitude)
                                const lng = parseFloat(entry.lng != null ? entry.lng : entry.longitude)
                                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
                                return {
                                    id: entry.id,
                                    title: entry.name,
                                    city: entry.city || entry.name,
                                    address: entry.address,
                                    coordinates: { lat, lng },
                                    sheet: entry.url,
                                }
                            }).filter(Boolean)

                            const center = stores.length
                                ? stores[0].coordinates
                                : { lat: 42.5, lng: 12.5 }

                            const map = new module.Maps({
                                element: element,
                                zoom: {
                                    value: 7,
                                    controls: {
                                        // Keep false: Maps.createView would add zoom twice if true
                                        enable: false,
                                    }
                                },
                                coordinates: {
                                    lat: center.lat,
                                    lng: center.lng
                                },
                                ui: false,
                                wheel: false,
                                fullscreen: false,
                                streetView: false,
                                mapTypeControl: false,
                                draggable: true,
                                popup: true,
                                stores: stores,
                            })

                            map.create({
                                search: null,
                                markers: stores.length > 0,
                            }, true)

                            if (map.view) {
                                map.view.whenReady(() => {
                                    requestAnimationFrame(() => {
                                        map.view.invalidateSize()
                                        if (typeof map.fitAll === 'function' && stores.length > 0) {
                                            map.fitAll()
                                        }
                                    })
                                })
                            }

                            element._storelocatorReady = true
                            element.addEventListener('storelocator:filter', (event) => {
                                const list = event.detail && event.detail.stores
                                if (typeof map.filterStores === 'function') {
                                    map.filterStores(list)
                                }
                            })
                            const queue = element._storelocatorQueue || []
                            queue.forEach((item) => {
                                if (typeof map.filterStores === 'function') {
                                    map.filterStores(item.stores)
                                }
                            })
                            element._storelocatorQueue = []
                        }
                    }).catch(error => {
                        console.warn('Error loading module:', error)
                    })
                }
            }
        ]
        
        
        modules.forEach((module, index) => {
            document.querySelectorAll(module.selector).forEach((element) => {
                if (module.check(element)) {
                    module.load(element)
                }
            })
        })
    }
}

new App()